// 模型背景（ScanKit 編輯頁「背景」分頁）：存在後端 model 的
// colorSwitch + color（純色）、bgImageSwitch + bgImage（背景圖），每個模型各自一份。
// 優先序：背景圖 > 純色 > 預設。判斷規則與 arviewer / areditor 的 utils/background.js 一致，改這裡要一起改。
//
// ScanKit 360 照片是拍攝影片切成的 JPG，背景本來就在照片裡；這裡的背景目前只會出現在照片外圍，
// 之後照片去背（透明）後，物件後面也會自動顯示這個背景。

export const BACKGROUND_MODES = ["default", "color", "image"];

// 第一次切到「純色」時預先選的顏色：白色物件最常需要的是深色背景
export const INITIAL_PICK_COLOR = "#333333";

// 色票：淺到深，加幾個常見的商品攝影底色（與 areditor 相同）
export const COLOR_SWATCHES = [
  "#FFFFFF",
  "#F2F2F2",
  "#BDBDBD",
  "#757575",
  "#333333",
  "#000000",
  "#1F2A44",
  "#E8DCCB",
];

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export const isValidHexColor = (value) => typeof value === "string" && HEX_COLOR.test(value);

// 使用者輸入的色碼：容許少打 #、前後空白，轉成大寫；不合法回傳 null
export const normalizeHexInput = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return isValidHexColor(withHash) ? withHash.toUpperCase() : null;
};

// 公開頁（已發佈的 model）要顯示的背景
export const getModelBackground = (model) => {
  if (model?.bgImageSwitch === true && typeof model.bgImage === "string" && model.bgImage.trim()) {
    return { type: "image", url: model.bgImage.trim() };
  }
  if (model?.colorSwitch === true && isValidHexColor(model.color)) {
    return { type: "color", color: model.color.toUpperCase() };
  }
  return { type: "default" };
};

const cssUrl = (url) => `url("${url.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}")`;

// CSS background；預設回傳 undefined，表示不覆蓋頁面原本的樣式
export const toCssBackground = (background) => {
  if (background?.type === "image")
    return `#FFFFFF ${cssUrl(background.url)} center / cover no-repeat`;
  if (background?.type === "color") return background.color;
  return undefined;
};

// 由後端 model 推出編輯頁的背景狀態
export const getBackgroundStateFromModel = (model) => {
  const shown = getModelBackground(model);
  const savedColor = isValidHexColor(model?.color) ? model.color.toUpperCase() : null;
  // 沒開過純色的模型 color 是後端預設 #FFFFFF，對白色物件沒幫助，改給深灰當起始值
  let color = INITIAL_PICK_COLOR;
  if (model?.colorSwitch === true && savedColor) color = savedColor;
  else if (savedColor && savedColor !== "#FFFFFF") color = savedColor;
  return {
    mode: shown.type,
    color,
    bgImage: typeof model?.bgImage === "string" ? model.bgImage.trim() : "",
    pendingFile: null,
    previewUrl: "",
  };
};

// 儲存時送給 updateModel 的欄位（不含上傳圖片本身）
export const toUpdateModelBody = (state, uploadedImageUrl) => {
  const imageUrl = uploadedImageUrl || state.bgImage;
  return {
    color: isValidHexColor(state.color) ? state.color : INITIAL_PICK_COLOR,
    colorSwitch: state.mode === "color",
    // 沒有圖可用時不能打開背景圖開關，否則公開頁會拿到空網址
    bgImageSwitch: state.mode === "image" && Boolean(imageUrl),
  };
};

// 編輯頁預覽用；圖片優先用尚未上傳的本機預覽
export const toPreviewBackground = (state) => {
  if (state?.mode === "image") {
    const url = state.previewUrl || state.bgImage;
    return url ? toCssBackground({ type: "image", url }) : undefined;
  }
  if (state?.mode === "color" && isValidHexColor(state.color)) return state.color;
  return undefined;
};
